// RFC 4180 CSV utilities. UTF-8 BOM is prepended so Excel auto-detects encoding.

const NEEDS_QUOTING = /[",\r\n]/;
const CRLF = '\r\n';

// Characters that make a spreadsheet read a cell as a formula rather than as text.
// Tab and carriage return are here because Excel strips leading whitespace before
// deciding, so "\t=cmd" is a formula too.
const FORMULA_LEAD = /^[=+\-@\t\r]/;

// A number is not a formula. `-5` and `+1.5` lead with a formula character and are
// values a spreadsheet should keep as values -- prefixing them would turn every
// negative figure in an export into text, which breaks sorting and arithmetic on
// exactly the columns people export to do arithmetic on.
const PLAIN_NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;

export function escapeCell(value) {
  if (value === null || value === undefined) return '';
  let str = typeof value === 'string' ? value : String(value);
  // Neutralise before quoting, so the guard applies whether or not the cell also
  // needs RFC 4180 quoting for its own reasons.
  //
  // Without this a cell reaches Excel or Sheets as a formula: `=cmd|'/C calc'!A0`
  // is the textbook case, and `@SUM(A1:A9)` the quieter one. These exports carry
  // customer data by definition, so the content is not ours to trust.
  //
  // A leading apostrophe is the conventional fix: spreadsheets read it as "treat
  // the rest as text" and do not show it in the cell.
  if (FORMULA_LEAD.test(str) && !PLAIN_NUMBER.test(str)) {
    str = `'${str}`;
  }
  if (!NEEDS_QUOTING.test(str)) return str;
  return `"${str.replace(/"/g, '""')}"`;
}

function buildRow(cells) {
  return cells.map(escapeCell).join(',');
}

export function toCsv(columns, rows, formatValue) {
  const fmt = typeof formatValue === 'function' ? formatValue : (v) => v;
  const lines = [buildRow(columns)];
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    const cells = columns.map((col) => fmt(row?.[col], col, row));
    lines.push(buildRow(cells));
  }
  return lines.join(CRLF);
}

// Chunked variant for large datasets — yields to the event loop between chunks
// via requestAnimationFrame so the UI stays responsive.
export function toCsvAsync(columns, rows, formatValue, { chunkSize = 2000 } = {}) {
  const fmt = typeof formatValue === 'function' ? formatValue : (v) => v;
  return new Promise((resolve) => {
    const parts = [buildRow(columns)];
    let i = 0;
    const tick = () => {
      const end = Math.min(i + chunkSize, rows.length);
      for (; i < end; i += 1) {
        const row = rows[i];
        const cells = columns.map((col) => fmt(row?.[col], col, row));
        parts.push(buildRow(cells));
      }
      if (i < rows.length) {
        // raf is missing in jsdom — fall through to setTimeout when undefined.
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(tick);
        else setTimeout(tick, 0);
      } else {
        resolve(parts.join(CRLF));
      }
    };
    tick();
  });
}

// Rows that are already arrays, written in the order they arrive.
//
// `toCsvAsync` above keys each row by column name, which silently loses data when two
// columns share a name: `Object.fromEntries` keeps the last one, so a row of
// ['Ann','West','Smith'] under ['Name','Region','Name'] writes 'Smith,West,Smith'.
// Display titles are human-readable and do collide -- `employee_name` and
// `employeename` both title to "Employee Name" -- so any payload that is already
// positional should stay positional rather than round-trip through an object.
export function toCsvRowsAsync(header, rows, { chunkSize = 2000 } = {}) {
  return new Promise((resolve) => {
    const parts = [buildRow(header)];
    let i = 0;
    const tick = () => {
      const end = Math.min(i + chunkSize, rows.length);
      for (; i < end; i += 1) parts.push(buildRow(rows[i] || []));
      if (i < rows.length) {
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(tick);
        else setTimeout(tick, 0);
      } else {
        resolve(parts.join(CRLF));
      }
    };
    tick();
  });
}

// Outcome of each separately requested check, as written into the export. Kept as its
// own section below the results so a check that matched nothing still shows up in the
// file, without inventing a data row for it.
const CHECK_OUTCOME_LABELS = {
  matches: 'Completed, matches found',
  no_matches: 'Completed, no matches',
  not_run: 'Not completed',
};

export function checkOutcomeLabel(status) {
  return CHECK_OUTCOME_LABELS[status] || CHECK_OUTCOME_LABELS.not_run;
}

export function checksToCsvSection(checks) {
  if (!Array.isArray(checks) || checks.length === 0) return '';
  const lines = [buildRow(['Requested checks']), buildRow(['Check', 'Outcome', 'Matches', 'Note'])];
  for (const c of checks) {
    const count = Number.isInteger(c?.match_count) ? c.match_count : '';
    lines.push(buildRow([c?.name || '', checkOutcomeLabel(c?.status), count, c?.reason || '']));
  }
  return lines.join(CRLF);
}

export function appendChecksSection(csvText, checks) {
  const section = checksToCsvSection(checks);
  if (!section) return csvText;
  return csvText ? `${csvText}${CRLF}${CRLF}${section}` : section;
}

export function buildCsvFilename(base) {
  const safe = (base || 'export')
    .toString()
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'export';
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${safe}_${yyyy}-${mm}-${dd}.csv`;
}

const BOM = '﻿';

export function downloadCsv(csvText, filename) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const blob = new Blob([BOM + csvText], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
